/**
 * Planning domain types (canon 11).
 * Align with supabase/migrations/054_planning_tables.sql.
 * Work Type and Execution Recipe fields: Canon Doc 21, migration 287.
 */

/**
 * Upstream input bound to a Plan Cycle.
 *
 * Product direction (2026-06-27) — two lanes:
 * - `published_priority_list`: ratified strategic priorities → PoR Strategic Priorities tab
 * - `provisional_plan`: exploratory planning before priorities are locked (saved queries,
 *   item pools, collections); planning detail may inform reprioritization
 *
 * `item_pool` remains for legacy cycles; new exploratory work should use `provisional_plan`.
 */
export type PlanningInputType =
  | 'published_priority_list'
  | 'provisional_plan'
  | 'item_pool'
  | 'collection'
  | 'previous_carry_forward';
export type PlanCycleStatus = 'draft' | 'active' | 'closed' | 'superseded';
export type PlanOfRecordStatus = 'active' | 'superseded';
export type PlanVersionChangeReason = 'constraint_shift' | 'assumption_break' | 'scope_adjustment' | 'other';
export type InitiativeStatus = 'planned' | 'in_progress' | 'complete' | 'dropped';
export type InitiativeDropReason = 'scope_removed' | 'deprioritized' | 'infeasible' | 'other';
export type WorkUnitReadinessState = 'not_ready' | 'ready' | 'in_progress' | 'done' | 'dropped';
export type DependencyTypeEnum = 'FS' | 'SS' | 'FF' | 'SF';
/** Plan-version assumptions — extended for Scenario Planner (unvalidated | confirmed | broken). Legacy valid/invalidated kept for existing rows. */
export type AssumptionStatus =
  | 'valid'
  | 'invalidated'
  | 'unvalidated'
  | 'confirmed'
  | 'broken';
export type PlanStartCondition = 'immediate' | 'date' | 'condition_based';

export interface PlanCycle {
  id: string;
  name: string;
  input_type: PlanningInputType;
  input_reference_id: string | null;
  input_version_id: string | null;
  input_snapshot_at: string | null;
  status: PlanCycleStatus;
  start_condition: PlanStartCondition;
  created_at: string;
  closed_at: string | null;
  workspace_id: string | null;
  created_by: string | null;
  /** Workspace primary — Plan of Record home; at most one true per workspace. */
  is_primary?: boolean;
  /** Set when reprioritization is opened (Doc 18 §6.1, migration 079). */
  governance_state?: string | null;
  /** When reprioritization was recommended (Doc 18 §6.1). */
  reprioritization_recommended_at?: string | null;
}

/**
 * Cycles that can still receive plan-version commits. Closed/superseded cycles
 * stay readable and simulation stays open — only commits into them are blocked.
 */
export function isPlanCycleCommitTarget(status: PlanCycleStatus): boolean {
  return status === 'active' || status === 'draft';
}

/** Prefer primary, then active, then newest. */
export function sortPlanCyclesForDisplay(cycles: PlanCycle[]): PlanCycle[] {
  const statusRank = (s: PlanCycleStatus): number => {
    switch (s) {
      case 'active': return 0;
      case 'draft': return 1;
      case 'closed': return 2;
      default: return 3;
    }
  };
  return [...cycles].sort((a, b) => {
    if (Boolean(a.is_primary) !== Boolean(b.is_primary)) return a.is_primary ? -1 : 1;
    const sr = statusRank(a.status) - statusRank(b.status);
    if (sr !== 0) return sr;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

export function resolvePrimaryPlanCycle(cycles: PlanCycle[]): PlanCycle | null {
  if (cycles.length === 0) return null;
  const primary = cycles.find((c) => c.is_primary);
  if (primary) return primary;
  return sortPlanCyclesForDisplay(cycles)[0] ?? null;
}

export interface PlanVersion {
  id: string;
  plan_cycle_id: string;
  version_number: number;
  created_at: string;
  created_by: string | null;
  change_reason: PlanVersionChangeReason | null;
  /** The decision that caused this version (change-course replan); the change log links it as the why. */
  source_decision_id?: string | null;
  payload?: Record<string, unknown>;
}

/** plan_constraint_evaluations (Doc 15, 16). */
export interface PlanConstraintEvaluation {
  id: string;
  plan_cycle_id: string;
  plan_version_id: string;
  evaluated_at: string;
  evaluated_by: string | null;
  algorithm_version: string;
  status: string;
  summary: Record<string, unknown>;
  details: Record<string, unknown>;
  created_at: string;
}

/** rank_sequence_divergences (Doc 15 §4, Doc 18 §4.1). */
export interface RankSequenceDivergence {
  id: string;
  plan_version_id: string;
  input_reference_id: string;
  payload: Record<string, unknown>;
  caused_by_evaluation_id: string | null;
  created_at: string;
  /** Column (Doc 18 §4.1): detected | accepted | mitigated | disputed. */
  resolution_status?: string | null;
  resolution_reason?: string | null;
}

/** tradeoff_decisions (Doc 15, 17). */
export interface TradeoffDecision {
  id: string;
  plan_version_id: string;
  tradeoff_kind: string;
  payload: Record<string, unknown>;
  accepted_by: string | null;
  accepted_at: string | null;
  created_at: string;
}

export interface PlanOfRecord {
  id: string;
  plan_cycle_id: string;
  plan_version_id: string;
  authorized_at: string;
  authorized_by: string | null;
  status: PlanOfRecordStatus;
}

export interface Initiative {
  id: string;
  plan_version_id: string;
  item_id: string;
  /** Display title from source item at plan creation (denormalized). */
  title?: string | null;
  work_profile: string | null;
  priority_position: number | null;
  status: InitiativeStatus;
  drop_reason: string | null;
}

export interface WorkUnit {
  id: string;
  initiative_id: string;
  title: string;
  description: string | null;
  readiness_state: WorkUnitReadinessState;
  effort_score: number | null;
  duration_score: number | null;
  estimated_effort_hours: number | null;
  estimated_duration_days: number | null;
  complexity_score: number | null;
  uncertainty_score: number | null;
  risk_score: number | null;
  buffer_score: number | null;
  confidence_score: number | null;
  coordination_load_score: number | null;
  skill_specificity_score: number | null;
  /**
   * FK → execution_recipe_templates.id (Canon Doc 21, migration 287).
   * Null = recipe_pending state; WorkUnit cannot be scheduled until resolved.
   * Immutable once set for a given PlanVersion.
   */
  resolved_recipe_id: string | null;
}

export interface DependencyLink {
  id: string;
  from_work_unit_id: string;
  to_work_unit_id: string;
  dependency_type: DependencyTypeEnum;
}

export interface CapacityAllocation {
  id: string;
  plan_version_id: string;
  resource_identifier: string;
  allocation_percentage: number;
  time_window_start: string | null;
  time_window_end: string | null;
}

export interface Milestone {
  id: string;
  plan_version_id: string;
  name: string;
  target_date: string | null;
  success_criteria: string | null;
}

export interface Assumption {
  id: string;
  plan_version_id: string;
  statement: string;
  status: AssumptionStatus;
  /** Engine-synced rows use `tp:<assumptionId>`; null = human-entered. */
  source_key?: string | null;
}

export interface Risk {
  id: string;
  plan_version_id: string;
  description: string;
  impact_score: number | null;
  likelihood_score: number | null;
  mitigation_plan: string | null;
}

/** Input to create a PlanCycle and initial POR (version 0) from a Published List, Item Pool, or previous carry forward (Doc 13 §6) */
export interface CreatePlanCycleInput {
  name: string;
  input_type: PlanningInputType;
  /** Required for published_priority_list and item_pool; omitted for previous_carry_forward (service sets to source cycle id). */
  input_reference_id?: string | null;
  input_version_id?: string | null;
  input_snapshot_at?: string | null;
  start_condition?: PlanStartCondition;
  created_by?: string | null;
}

/** PlanOfRecord snapshot payload (Doc 16 §3). Immutable reporting read model. */
export interface PlanOfRecordSnapshotPayload {
  initiatives: Initiative[];
  work_units: WorkUnit[];
  dependency_links: DependencyLink[];
  capacity_allocations: CapacityAllocation[];
  milestones: Milestone[];
  assumptions: Assumption[];
  risks: Risk[];
  critical_path_work_unit_ids: string[];
  readiness: { work_unit_id: string; ready: boolean; blockers: string[] }[];
  next_right_ranking?: { work_unit_id: string; title: string; score: number }[];
}

export interface PlanOfRecordSnapshot {
  id: string;
  plan_cycle_id: string;
  plan_of_record_id: string;
  plan_version_id: string;
  input_reference_id: string;
  authorized_at: string;
  authorized_by: string | null;
  superseded_by_snapshot_id: string | null;
  audit_event_id: string | null;
  created_at: string;
  payload: PlanOfRecordSnapshotPayload;
}

/**
 * Authoritative current POR (canon 10, 11): the active plan_of_record_snapshots row
 * where superseded_by_snapshot_id IS NULL. Use this instead of plan_of_record for reads.
 */
export type ActivePlanOfRecord = PlanOfRecordSnapshot;

/** Result of loadPlanningInputSnapshot (Phase 1). Read-only prioritization snapshot. */
export interface PlanningInputSnapshot {
  itemIds: string[];
  inputReferenceId: string | null;
  titleMap: Record<string, string>;
}

/** DTO returned by createPlanCycleWithInitialVersion (Phase 1). Payload is canonical. */
export interface Phase1PlanningDto {
  planCycle: PlanCycle;
  planVersion: PlanVersion;
  payload: PlanOfRecordSnapshotPayload;
}

// ============================================================================
// Plan of Record living document types (migration 260)
// ============================================================================

/** Certainty lane for an initiative within a PoR. */
export type CertaintyLevel = 'high' | 'medium' | 'low';
/** Whether certainty was computed from evaluation or set manually by a planner. */
export type CertaintySource = 'derived' | 'manual';

/** Narrative context fields on plan_cycles — the multi-dimensional PoR narrative. */
export interface PlanCycleContext {
  vision_statement: string | null;
  goals: string[];
  principles: string[];
  historical_context: string | null;
  time_horizon: string | null;
  org_context: string | null;
}

/** Extended PlanCycle with PoR narrative context. */
export interface PlanCycleWithContext extends PlanCycle {
  vision_statement: string | null;
  goals: string[];
  principles: string[];
  historical_context: string | null;
  time_horizon: string | null;
  org_context: string | null;
}

/** Open question / known unknown living in a Plan of Record. */
export type OpenQuestionStatus = 'open' | 'in_progress' | 'resolved' | 'deferred';

export interface PlanOpenQuestion {
  id: string;
  plan_cycle_id: string;
  question: string;
  status: OpenQuestionStatus;
  resolution: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Initiative with certainty lane fields (migration 260). */
export interface InitiativeWithCertainty extends Initiative {
  certainty_level: CertaintyLevel;
  certainty_source: CertaintySource;
}

/** Full PoR read model for the detail page — all dimensions aggregated. */
export interface PorDimensions {
  cycle: PlanCycleWithContext;
  latestVersion: PlanVersion | null;
  activePor: ActivePlanOfRecord | null;
  initiatives: InitiativeWithCertainty[];
  openQuestions: PlanOpenQuestion[];
  latestEvaluation: PlanConstraintEvaluation | null;
  divergences: RankSequenceDivergence[];
  tradeoffs: TradeoffDecision[];
  auditEvents: PlanningAuditEvent[];
  certaintyCounts: { high: number; medium: number; low: number };
}

/** Single planning audit event (planning_audit_events table, migration 055). */
export interface PlanningAuditEvent {
  id: string;
  occurred_at: string;
  event_type: string;
  plan_cycle_id: string | null;
  plan_version_id: string | null;
  plan_of_record_id: string | null;
  actor_id: string | null;
  reason: string | null;
  payload: Record<string, unknown>;
}

// Re-export Work Type and Execution Recipe types (Canon Doc 21)
export * from './work-type-recipes';
